
import { OpenDeliveryOrder, OpenDeliveryEvent } from '../types/openDelivery';
import { OpenDeliveryConfig, SystemConfig } from '../types';

// --- DATA POOLS FOR REALISM ---
const CUSTOMER_NAMES = ["Ricardo Oliveira", "Ana Beatriz Silva", "Carlos Eduardo Lima", "Mariana Costa", "Felipe Santos", "Juliana Arroxelas", "Marcos Mello", "Tereza Cavalcante", "Gustavo Pontes", "Beatriz Farias"];

const ITEMS_POOL = [
    { name: 'Combo S.A.L. Classic', price: 45.90, desc: 'Hambúrguer, Batata e Refri' },
    { name: 'Double Cheese Bacon', price: 38.00, desc: 'Dois blends de 160g' },
    { name: 'Smash Salad', price: 24.00, desc: 'Pão brioche, carne e salada' },
    { name: 'Batata Rústica G', price: 18.50, desc: 'Com alecrim e sal grosso' },
    { name: 'Milkshake de Ninho', price: 22.00, desc: '500ml de pura cremosidade' }
];

const NEIGHBORHOODS_GENERIC = [
    { name: 'Centro', street: 'Rua Principal', number: '100' },
    { name: 'Bairro Nobre', street: 'Avenida das Palmeiras', number: '500' },
    { name: 'Residencial', street: 'Rua das Flores', number: '25' },
    { name: 'Industrial', street: 'Estrada Velha', number: '1200' },
    { name: 'Parque', street: 'Alameda dos Lagos', number: '450' }
];

/**
 * v4.9.7: Polling de simulação dinâmico (Adaptado ao Onboarding SaaS)
 */
export const pollSimulation = async (merchantId: string, systemConfig?: SystemConfig): Promise<OpenDeliveryOrder[]> => {
    const isFar = Math.random() > 0.75; 
    const location = NEIGHBORHOODS_GENERIC[Math.floor(Math.random() * NEIGHBORHOODS_GENERIC.length)];
    
    // v4.9.7: Usa as coordenadas da loja configurada, ou SP como fallback
    const hubLat = systemConfig?.businessLat || -23.55052;
    const hubLng = systemConfig?.businessLng || -46.63330;
    const storeName = systemConfig?.storeName || 'Minha Operação S.A.L.';
    
    const radiusScale = isFar ? 0.08 : 0.02; 
    const latOffset = (Math.random() - 0.5) * radiusScale;
    const lngOffset = (Math.random() - 0.5) * radiusScale;
    
    const id = Math.random().toString(36).substr(2, 9);
    const orderNumber = Math.floor(Math.random() * 9000) + 1000;
    const randomName = CUSTOMER_NAMES[Math.floor(Math.random() * CUSTOMER_NAMES.length)];
    const randomItem = ITEMS_POOL[Math.floor(Math.random() * ITEMS_POOL.length)];
    
    const isIfood = Math.random() > 0.5;
    const randomChannel = isIfood ? "iFood" : (Math.random() > 0.5 ? "Rappi" : "Site Próprio");
    
    let orderObservation = "";
    if (isIfood) {
        const supportId = Math.floor(Math.random() * 90000000 + 10000000);
        orderObservation = `Pedido iFood Nº ${orderNumber}\nTelefone do cliente: 0800 705 1020 ID: ${supportId}\nLoja do iFood: ${storeName}`;
    }

    const paymentMethods = [
        { type: 'CASH', prepaid: false, label: 'Dinheiro' },
        { type: 'PIX', prepaid: true, label: 'PIX Online' },
        { type: 'CREDIT', prepaid: true, label: 'Cartão Online' },
        { type: 'DEBIT', prepaid: false, label: 'Maquininha Débito' }
    ];
    const pMethod = paymentMethods[Math.floor(Math.random() * paymentMethods.length)];

    return [{
        id,
        displayId: orderNumber.toString(),
        createdAt: new Date().toISOString(),
        type: 'DELIVERY',
        status: 'CREATED',
        orderTiming: 'INSTANT',
        salesChannel: randomChannel,
        observation: orderObservation, 
        merchant: { id: merchantId, name: storeName },
        customer: { 
            id: 'cust-' + id, 
            name: randomName, 
            phone: { number: isIfood ? '08007051020' : '119' + Math.floor(90000000 + Math.random() * 9000000) } 
        },
        items: [
            { 
                id: 'i1', index: 1, name: randomItem.name, quantity: 1, 
                unitPrice: { value: randomItem.price, currency: 'BRL' }, 
                totalPrice: { value: randomItem.price, currency: 'BRL' }
            }
        ],
        total: { 
            itemsPrice: { value: randomItem.price, currency: 'BRL' }, 
            deliveryFee: { value: isFar ? 12.00 : 7.00, currency: 'BRL' }, 
            orderAmount: { value: randomItem.price + (isFar ? 12.00 : 7.00), currency: 'BRL' } 
        },
        payments: { 
            prepaid: pMethod.prepaid ? randomItem.price + (isFar ? 12.00 : 7.00) : 0, 
            pending: pMethod.prepaid ? 0 : randomItem.price + (isFar ? 12.00 : 7.00), 
            methods: [{ 
                value: randomItem.price + (isFar ? 12.00 : 7.00), 
                currency: 'BRL', 
                type: pMethod.type as any, 
                method: pMethod.type, 
                prepaid: pMethod.prepaid
            }] 
        },
        delivery: { 
            deliveredBy: 'MERCHANT',
            deliveryAddress: { 
                street: location.street, 
                number: location.number, 
                district: location.name, 
                city: 'Cidade Exemplo', 
                state: 'EX', 
                country: 'BR',
                formattedAddress: `${location.street}, ${location.number} - ${location.name}`,
                coordinates: { 
                    latitude: hubLat + latOffset, 
                    longitude: hubLng + lngOffset 
                }
            },
            pickupCode: isIfood ? 'IFD-' + orderNumber : undefined
        }
    } as any];
};

// ... restante do arquivo preservado ...

// Validação de credenciais e handshake inicial para o modo produção
export const testAuthConnection = async (config: OpenDeliveryConfig): Promise<boolean> => {
    if (config.mode === 'simulation') return true;
    if (!config.baseUrl || !config.clientId || !config.clientSecret) return false;
    
    try {
        const response = await fetch(`${config.baseUrl}/v1/merchant/oauth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                grant_type: 'client_credentials',
                client_id: config.clientId,
                client_secret: config.clientSecret
            })
        });
        return response.ok;
    } catch (e) {
        console.error("[OD] Erro no teste de autenticação:", e);
        return false;
    }
};

// Busca ativa de pedidos em aberto no integrador
export const pollOpenDeliveryOrders = async (config: OpenDeliveryConfig): Promise<OpenDeliveryOrder[]> => {
    if (!config.active || config.mode === 'simulation') return [];
    
    try {
        const response = await fetch(`${config.baseUrl}/v1/merchant/orders`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${config.clientSecret}`,
                'Accept': 'application/json'
            }
        });
        if (response.ok) {
            return await response.json();
        }
    } catch (e) {
        console.error("[OD] Erro no polling de pedidos:", e);
    }
    return [];
};

// Helper interno para disparar eventos de alteração de status para o integrador
const sendODSignal = async (config: OpenDeliveryConfig, orderId: string, action: string, body: any = {}): Promise<boolean> => {
    if (!config.active) return false;
    if (config.mode === 'simulation') return true;

    try {
        const response = await fetch(`${config.baseUrl}/v1/merchant/orders/${orderId}/${action}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${config.clientSecret}`
            },
            body: JSON.stringify(body)
        });
        return response.ok;
    } catch (e) {
        console.error(`[OD] Erro ao enviar sinal (${action}):`, e);
        return false;
    }
};

// Sinais disparados pelas ações do Cockpit (Aceite, Pronto, Despacho)
export const sendConfirmSignal = (config: OpenDeliveryConfig, orderId: string, preparationTime: number) => 
    sendODSignal(config, orderId, 'confirm', { preparationTime });

export const sendReadySignal = (config: OpenDeliveryConfig, orderId: string) => 
    sendODSignal(config, orderId, 'ready_for_pickup');

export const sendDispatchSignal = (config: OpenDeliveryConfig, orderId: string) => 
    sendODSignal(config, orderId, 'dispatch');

// Sinais disparados pelo App do Piloto (Coleta e Entrega Concluída)
export const sendPickedUpSignal = (config: OpenDeliveryConfig, orderId: string) => 
    sendODSignal(config, orderId, 'picked_up');

export const sendDeliveredSignal = (config: OpenDeliveryConfig, orderId: string) => 
    sendODSignal(config, orderId, 'delivered');
